import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import { IPart } from "../../models/Part";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";

const Row = ({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={classNames("text-sm", bold ? "font-semibold text-gray-900" : "text-gray-500")}>{label}</span>
    <span className={classNames("text-sm text-gray-900", bold && "font-semibold")}>{value}</span>
  </div>
);

export default function VehicleFinancialSummary({ vehicle, price = null }: { vehicle: IVehicle; price?: number | null }) {
  const { patchVehicle } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Financial Decision");

  const parts = vehicle.parts ?? [];
  const partPrice = (part: IPart) => part.price ?? part.enteredPrice ?? 0;
  const coreValue = parts.filter((part) => part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const partsValue = parts.filter((part) => !part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const total = partsValue + coreValue;

  const decision = vehicle.financialDecision ?? FinancialDecision.None;

  const setDecision = (next: FinancialDecision) => {
    // Clicking the active choice again clears it back to None.
    const value = decision === next ? FinancialDecision.None : next;
    patchVehicle.mutate({ vehicleId: vehicle.id, patch: { financialDecision: value } }, saveHandlers);
  };

  const cardColour =
    decision === FinancialDecision.Target
      ? "border-green-300 bg-green-50"
      : decision === FinancialDecision.Rejected
        ? "border-red-300 bg-red-50"
        : "border-gray-200 bg-white";

  return (
    <div className={classNames("rounded-lg border shadow-sm p-4", cardColour)}>
      <h2 className="text-lg font-medium text-gray-900 mb-2">Financial Summary</h2>

      <Row label="Vehicle Price" value={price != null ? PriceFormat(price) : "N/A"} />
      <Row label="Parts Value" value={PriceFormat(partsValue)} />
      <Row label="Core Value" value={PriceFormat(coreValue)} />
      <Row label="Scrap Price" value="—" />

      <hr className="my-2 border-gray-200" />
      <Row label="Total" value={PriceFormat(total)} bold />

      <div className="flex gap-2 pt-3">
        <button
          type="button"
          onClick={() => setDecision(FinancialDecision.Target)}
          disabled={patchVehicle.isLoading}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
            decision === FinancialDecision.Target
              ? "border-transparent bg-green-600 text-white hover:bg-green-700"
              : "border-green-500 text-green-700 hover:bg-green-50"
          )}
        >
          Target
        </button>
        <button
          type="button"
          onClick={() => setDecision(FinancialDecision.Rejected)}
          disabled={patchVehicle.isLoading}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
            decision === FinancialDecision.Rejected
              ? "border-transparent bg-red-600 text-white hover:bg-red-700"
              : "border-red-500 text-red-700 hover:bg-red-50"
          )}
        >
          Rejected
        </button>
      </div>
    </div>
  );
}
